import FormTitle from 'components/PageLayout/FormTitle';
import {
  IWelcomeFormValues,
  WelcomeFormFields,
} from 'components/curo-components/forms/WelcomeForm';
import WelcomeForm from 'components/curo-components/forms/WelcomeForm';
import { GoogleAnalyticsEventType } from 'enums/GoogleAnalyticsEventType';
import { PrescreenedOfferType } from 'enums/curo/PrescreenedOfferType';
import {
  setContactInformationData,
  setWelcomeProcessData,
} from 'handlers/curo-handlers/applyProcessSlice';
import { getApplyProcessState } from 'handlers/curo-handlers/selectors';
import { setEmail, setPhone } from 'handlers/curo-handlers/signInProcessSlice';
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useGoogleAnalytics from 'hooks/useGoogleAnalytics';
import useUtmSource from 'hooks/useUtmSource';
import { IDefaultStepProps } from 'pages/CuroPages/types';
import { submitMailOffer } from 'thunks';

interface IWelcomeStepProps extends IDefaultStepProps {
  onStepEnd: () => void;
}

const FORM_TITLE = 'Welcome!';
const FORM_SUBTITLE = (
  <>
    You are in the right place to find a personal loan that meets your needs.
    <br />
    Let’s find your pre-approved offer. We just need a little information to get started.
  </>
);

const WelcomeStep = ({ onStepComplete, onStepEnd }: IWelcomeStepProps) => {
  const { getUtmData } = useUtmSource();
  const trackEvent = useGoogleAnalytics();

  const { welcomeProcessData } = useAppSelector(getApplyProcessState);
  const dispatch = useAppDispatch();
  const dispatchWithUnwrap = useDispatchWithUnwrap();

  const handleSubmit = async (values: IWelcomeFormValues) => {
    await dispatch(setWelcomeProcessData(values));

    const utmData = getUtmData();

    const { actionsType, decisionId, summitEncryptedLeadId } = await dispatchWithUnwrap(
      submitMailOffer({
        borrowerEmail: values[WelcomeFormFields.BorrowerEmail],
        borrowerPhone: values[WelcomeFormFields.BorrowerPhone],
        borrowerLast6DigitsSSN: values[WelcomeFormFields.BorrowerLast6DigitsSSN],
        borrowerZipOrPostalCode: values[WelcomeFormFields.BorrowerZipOrPostalCode],
        ...utmData,
      }),
    );

    trackEvent(GoogleAnalyticsEventType.DmMatchResult, {
      prescreenedOfferType: actionsType,
      decisionId,
    });

    if (actionsType === PrescreenedOfferType.Online) {
      await dispatch(
        setContactInformationData({
          borrowerPhone: values[WelcomeFormFields.BorrowerPhone],
          borrowerEmail: values[WelcomeFormFields.BorrowerEmail],
          telephoneAndElectronicCommunicationPolicy:
            values[WelcomeFormFields.TelephoneAndElectronicCommunicationPolicy],
        }),
      );
      await dispatch(setPhone({ phone: values[WelcomeFormFields.BorrowerPhone] }));
      await dispatch(setEmail({ email: values[WelcomeFormFields.BorrowerEmail] }));
    }

    switch (actionsType) {
      case PrescreenedOfferType.Online:
      case PrescreenedOfferType.NoMatch:
        return onStepEnd();
      case PrescreenedOfferType.Branch:
        trackEvent(GoogleAnalyticsEventType.DmBranchOfferWithSummit, { summitEncryptedLeadId });
        return onStepComplete();
      default:
        return;
    }
  };

  return (
    <>
      <FormTitle title={FORM_TITLE} subTitle={FORM_SUBTITLE} />

      <WelcomeForm handleSubmit={handleSubmit} initialData={welcomeProcessData} />
    </>
  );
};

export default WelcomeStep;
